import { ref, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import router from '@/router'

/**
 * Composable for tracking user activity and handling inactivity logout
 * 
 * Handles both inactivity timeout (30 minutes) and absolute session timeout (5 hours)
 */
export function useActivityTracker() {
  const store = useStore()
  
  const isActive = ref(true)
  const lastActivity = ref(Date.now())
  const sessionStartTime = ref(null)
  const inactivityTimeout = ref(null)
  const warningTimeout = ref(null)
  const absoluteTimeout = ref(null)
  const absoluteWarningTimeout = ref(null)
  const absoluteCheckInterval = ref(null)
  const inactivityCountdown = ref(null)
  const absoluteCountdown = ref(null)
  const inactivityCountdownInterval = ref(null)
  const absoluteCountdownInterval = ref(null)
  
  const INACTIVITY_DURATION = 30 * 60 * 1000 // 30 minutes
  const WARNING_DURATION = 10 * 60 * 1000 // Show warning 10 minutes before logout
  const ABSOLUTE_SESSION_DURATION = 5 * 60 * 60 * 1000 // 5 hours
  const ABSOLUTE_WARNING_DURATION = 10 * 60 * 1000 // Show warning 10 minutes before absolute timeout
  
  const activityEvents = [
    'mousedown', 'mousemove', 'keypress', 'scroll', 
    'touchstart', 'click', 'keydown'
  ]
  
  
  const resetInactivityTimer = () => {
    lastActivity.value = Date.now()
    isActive.value = true
    
    // Clear only inactivity-related timeouts (not absolute timeout)
    if (inactivityTimeout.value) clearTimeout(inactivityTimeout.value)
    if (warningTimeout.value) clearTimeout(warningTimeout.value)
    
    // Clear inactivity warning notification and countdown if activity detected
    if (inactivityCountdownInterval.value) {
      clearInterval(inactivityCountdownInterval.value)
      inactivityCountdownInterval.value = null
      // Remove the inactivity warning notification
      store.dispatch('notifications/remove', 'inactivity-warning')
    }
    
    // Check for absolute timeout first (overrides activity detection)
    if (sessionStartTime.value) {
      const sessionDuration = Date.now() - sessionStartTime.value
      if (sessionDuration >= ABSOLUTE_SESSION_DURATION) {
        handleAbsoluteTimeout()
        return
      }
    }
    
    // Set warning timeout (15 seconds before logout for testing)
    warningTimeout.value = setTimeout(() => {
      showInactivityWarning()
    }, WARNING_DURATION)
    
    // Set logout timeout
    inactivityTimeout.value = setTimeout(() => {
      handleInactivityLogout()
    }, INACTIVITY_DURATION)
  }
  
  const showInactivityWarning = () => {
    // Initialize countdown
    inactivityCountdown.value = Math.floor(WARNING_DURATION / 1000) // 15 seconds
    
    // Format time as MM:SS
    const formatTime = (seconds) => {
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    
    // Show initial warning popup
    store.dispatch('notifications/add', {
      id: 'inactivity-warning',
      type: 'warning',
      title: 'Session Expiring Soon (Inactivity)',
      message: `You will be logged out in ${formatTime(inactivityCountdown.value)} due to inactivity.`,
      duration: 0 // Persistent until dismissed
    })
    
    // Start countdown interval
    inactivityCountdownInterval.value = setInterval(() => {
      inactivityCountdown.value -= 1
      
      // Update notification message (including when it reaches 00:00)
      store.dispatch('notifications/add', {
        id: 'inactivity-warning',
        message: `You will be logged out in ${formatTime(inactivityCountdown.value)} due to inactivity.`
      })
      
      // Clear interval and schedule notification removal when reaching 00:00
      if (inactivityCountdown.value <= 0) {
        clearInterval(inactivityCountdownInterval.value)
        inactivityCountdownInterval.value = null
        
        // Remove notification after 1 second
        setTimeout(() => {
          store.dispatch('notifications/remove', 'inactivity-warning')
        }, 1000)
      }
    }, 1000)
  }
  
  const showAbsoluteTimeoutWarning = () => {
    // Clear inactivity timeouts when absolute warning is shown
    if (inactivityTimeout.value) clearTimeout(inactivityTimeout.value)
    if (warningTimeout.value) clearTimeout(warningTimeout.value)
    if (inactivityCountdownInterval.value) clearInterval(inactivityCountdownInterval.value)
    
    // Set a flag to prevent showing warning multiple times
    absoluteWarningTimeout.value = true
    
    // Initialize countdown
    absoluteCountdown.value = Math.floor(ABSOLUTE_WARNING_DURATION / 1000)
    
    // Format time as MM:SS
    const formatTime = (seconds) => {
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    
    // Show initial warning popup
    store.dispatch('notifications/add', {
      id: 'absolute-timeout-warning',
      type: 'warning',
      title: 'Session Expiring Soon (Time Limit)',
      message: `You will be logged out in ${formatTime(absoluteCountdown.value)} due to maximum session limit.`,
      duration: 0 // Persistent until dismissed
    })
    
    // Start countdown interval
    absoluteCountdownInterval.value = setInterval(() => {
      absoluteCountdown.value -= 1
      
      // Update notification message (including when it reaches 00:00)
      store.dispatch('notifications/add', {
        id: 'absolute-timeout-warning',
        message: `You will be logged out in ${formatTime(absoluteCountdown.value)} due to maximum session limit.`
      })
      
      // Clear interval and schedule notification removal when reaching 00:00
      if (absoluteCountdown.value <= 0) {
        clearInterval(absoluteCountdownInterval.value)
        absoluteCountdownInterval.value = null
        
        // Remove notification after 1 second
        setTimeout(() => {
          store.dispatch('notifications/remove', 'absolute-timeout-warning')
        }, 1000)
      }
    }, 1000)
  }
  
  const handleInactivityLogout = async () => {
    // Clear absolute timeout when inactivity logout is triggered
    if (absoluteTimeout.value) clearTimeout(absoluteTimeout.value)
    if (absoluteWarningTimeout.value) clearTimeout(absoluteWarningTimeout.value)
    if (absoluteCheckInterval.value) clearInterval(absoluteCheckInterval.value)
    if (absoluteCountdownInterval.value) clearInterval(absoluteCountdownInterval.value)
    if (inactivityCountdownInterval.value) {
      clearInterval(inactivityCountdownInterval.value)
      // Remove the inactivity warning notification
      store.dispatch('notifications/remove', 'inactivity-warning')
    }
    
    // Logout user and redirect to login page
    try {
      await store.dispatch('auth/logout')
      router.push('/login?message=inactivity-logout')
    } catch (error) {
      console.error('Error during inactivity logout:', error)
      // Still redirect even if logout fails
      router.push('/login?message=inactivity-logout')
    }
  }
  
  const checkAbsoluteTimeout = () => {
    if (sessionStartTime.value) {
      const sessionDuration = Date.now() - sessionStartTime.value
      const timeUntilWarning = ABSOLUTE_SESSION_DURATION - ABSOLUTE_WARNING_DURATION
      const timeUntilTimeout = ABSOLUTE_SESSION_DURATION
      
      // Check if we should show warning
      if (sessionDuration >= timeUntilWarning && sessionDuration < timeUntilTimeout) {
        // Only show warning once
        if (!absoluteWarningTimeout.value) {
          showAbsoluteTimeoutWarning()
        }
      }
      
      // Check if we should trigger timeout
      if (sessionDuration >= timeUntilTimeout) {
        handleAbsoluteTimeout()
      }
    }
  }
  
  const handleAbsoluteTimeout = async () => {
    // Clear all timeouts when absolute timeout is triggered
    if (inactivityTimeout.value) clearTimeout(inactivityTimeout.value)
    if (warningTimeout.value) clearTimeout(warningTimeout.value)
    if (absoluteWarningTimeout.value) clearTimeout(absoluteWarningTimeout.value)
    if (absoluteCheckInterval.value) clearInterval(absoluteCheckInterval.value)
    if (absoluteCountdownInterval.value) {
      clearInterval(absoluteCountdownInterval.value)
      // Remove the absolute timeout warning notification
      store.dispatch('notifications/remove', 'absolute-timeout-warning')
    }
    if (inactivityCountdownInterval.value) {
      clearInterval(inactivityCountdownInterval.value)
      // Remove the inactivity warning notification
      store.dispatch('notifications/remove', 'inactivity-warning')
    }
    
    // Logout user and redirect to login page
    try {
      await store.dispatch('auth/logout')
      router.push('/login?message=session-timeout')
    } catch (error) {
      console.error('Error during absolute timeout logout:', error)
      // Still redirect even if logout fails
      router.push('/login?message=session-timeout')
    }
  }
  
  const setupActivityListeners = () => {
    activityEvents.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, true)
    })
  }
  
  const removeActivityListeners = () => {
    activityEvents.forEach(event => {
      document.removeEventListener(event, resetInactivityTimer, true)
    })
  }
  
  // Watch for login state changes
  watch(() => store.getters['auth/isLoggedIn'], (isLoggedIn) => {
    if (isLoggedIn) {
      // Add a small delay to prevent interference with OAuth login flow
      setTimeout(() => {
        // Initialize session start time
        sessionStartTime.value = Date.now()
        setupActivityListeners()
        resetInactivityTimer()
        
        // Set up periodic check for absolute timeout (every 1 second)
        absoluteCheckInterval.value = setInterval(() => {
          checkAbsoluteTimeout()
        }, 1000)
      }, 100)
    } else {
      removeActivityListeners()
      // Clear any existing timeouts
      if (inactivityTimeout.value) {
        clearTimeout(inactivityTimeout.value)
        inactivityTimeout.value = null
      }
      if (warningTimeout.value) {
        clearTimeout(warningTimeout.value)
        warningTimeout.value = null
      }
      if (absoluteTimeout.value) {
        clearTimeout(absoluteTimeout.value)
        absoluteTimeout.value = null
      }
      if (absoluteWarningTimeout.value) {
        clearTimeout(absoluteWarningTimeout.value)
        absoluteWarningTimeout.value = null
      }
      if (absoluteCheckInterval.value) {
        clearInterval(absoluteCheckInterval.value)
        absoluteCheckInterval.value = null
      }
      if (inactivityCountdownInterval.value) {
        clearInterval(inactivityCountdownInterval.value)
        inactivityCountdownInterval.value = null
      }
      if (absoluteCountdownInterval.value) {
        clearInterval(absoluteCountdownInterval.value)
        absoluteCountdownInterval.value = null
      }
      // Reset session start time
      sessionStartTime.value = null
    }
  }, { immediate: true }) // Run immediately on mount
  
  onUnmounted(() => {
    removeActivityListeners()
    if (inactivityTimeout.value) clearTimeout(inactivityTimeout.value)
    if (warningTimeout.value) clearTimeout(warningTimeout.value)
    if (absoluteTimeout.value) clearTimeout(absoluteTimeout.value)
    if (absoluteWarningTimeout.value) clearTimeout(absoluteWarningTimeout.value)
    if (absoluteCheckInterval.value) clearInterval(absoluteCheckInterval.value)
  })
  
  return {
    isActive,
    lastActivity,
    resetInactivityTimer
  }
}
